import { useEffect, useRef, useState } from "react";
import {
  Box,
  useMediaQuery,
  Card,
  Theme,
  lighten,
  Alert,
  AlertTitle,
} from "@mui/material";
import { Outlet, useLocation } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AdminSideNav, AdminAppHeader } from "../components/admin";
import { InsightsChatbotProvider, InsightsChatbotWidget } from "../../features/admin/insights";
import { DASHBOARD_PATH_TO_KEY } from "../utils";
import {
  useClientDashboardSettings,
  useCurrentClientDashboardSettings,
} from "../hooks";
import { ClientScopeProvider, useClientScope } from "../contexts";
import { clientManagementApi } from "../../features/admin/settings/api/clientManagement";
import { qk } from "../../lib/queryKeys";

const AdminLayoutInner = () => {
  const isMdUp = useMediaQuery("(min-width:900px)");
  const [drawerOpen, setDrawerOpen] = useState(isMdUp);
  const location = useLocation();
  const { data: dashboardSettings } = useCurrentClientDashboardSettings();
  const { effectiveClientId, isCentralAdmin } = useClientScope();
  const queryClient = useQueryClient();
  const previousClientIdRef = useRef<string | null | undefined>(undefined);

  const clientUserCountQuery = useQuery({
    queryKey: qk.clientUserCount(effectiveClientId),
    queryFn: () =>
      clientManagementApi.getClientUserCount(effectiveClientId as string),
    enabled: isCentralAdmin && !!effectiveClientId,
    staleTime: 2 * 60 * 1000,
  });

  const dashboardKey = DASHBOARD_PATH_TO_KEY[location.pathname];
  const dashboardSetting = dashboardKey
    ? dashboardSettings?.data?.find((setting) => setting.dashboard_key === dashboardKey)
    : null;
  const isDashboardDisabled =
    dashboardSetting?.is_enabled === false;

  useEffect(() => {
    setDrawerOpen(isMdUp);
  }, [isMdUp]);

  useEffect(() => {
    if (previousClientIdRef.current === effectiveClientId) return;
    previousClientIdRef.current = effectiveClientId;
    const roots = new Set([
      "metrics",
      "programme-profile",
      "jeopardy",
      "filter-options",
      "dashboard-settings",
      "admin",
    ]);

    queryClient.invalidateQueries({
      predicate: (query) => roots.has(String(query.queryKey[0])),
      refetchType: "active",
    });
  }, [effectiveClientId, queryClient]);

  return (
    <Box
      sx={{
        display: "flex",
        height: "100vh",
        backgroundColor: (theme: Theme) =>
          theme.palette.mode === "dark"
            ? lighten(theme.palette.background.default, 0.07)
            : theme.palette.action.hover,
      }}
    >
      <AdminSideNav
        onClose={() => setDrawerOpen(!drawerOpen)}
        open={drawerOpen}
      />

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 1.5,
          height: "100%",
        }}
      >
        <Card
          variant="outlined"
          sx={{
            border: "none",
            height: "100%",
            borderRadius: 1.5,
            boxShadow: 1,
            display: "flex",
            flexDirection: "column",
            backgroundColor: (theme: Theme) =>
              theme.palette.mode === "dark"
                ? lighten(theme.palette.background.default, 0.03)
                : theme.palette.background.paper,
          }}
        >
          <InsightsChatbotProvider>
            <AdminAppHeader
              onMenu={() => setDrawerOpen(!drawerOpen)}
              drawerOpen={drawerOpen}
            />

            <Box
              sx={{
                flex: 1,
                overflow: "auto",
                p: 0,
              }}
            >
              {isCentralAdmin &&
              effectiveClientId &&
              clientUserCountQuery.data?.data?.count === 0 ? (
                <Box sx={{ p: 3, pb: 0 }}>
                  <Alert severity="warning">
                    <AlertTitle>No users assigned</AlertTitle>
                    This client has no users assigned. Assign users in Settings to
                    see dashboard data.
                  </Alert>
                </Box>
              ) : null}
              {isDashboardDisabled ? (
                <Box sx={{ p: 3 }}>
                  <Alert severity="info">
                    This dashboard has been disabled for your client. Contact your
                    administrator to enable access.
                  </Alert>
                </Box>
              ) : (
                <Outlet />
              )}
            </Box>
            <InsightsChatbotWidget />
          </InsightsChatbotProvider>
        </Card>
      </Box>
    </Box>
  );
};

export const AdminLayout = () => {
  return (
    <ClientScopeProvider>
      <AdminLayoutInner />
    </ClientScopeProvider>
  );
};
